// FINAL PATH: client/src/components/mis/ZoomSlider.tsx
// STAGING: client/src/MIS_work_2_frontend/mfd/components/ZoomSlider.tsx
// OWNER: Person 2 (MIS_work_2)
//
// The vertical zoom control used by the coverage map and by every chart on the
// page, so the two behave and look identical: step in, drag, step out, reset,
// with a readout of where you are.
//
// Vertical because everything it controls is vertical, and oriented so that UP
// always means "zoom in / magnify". A rotated horizontal range input is the
// portable way to get a vertical slider - the rotated element keeps its
// unrotated layout box, so the fixed-size wrapper is what reserves the space.

import { Maximize2, Minus, Plus } from 'lucide-react';

import { Button } from '@/components/ui';
import cn from '@/utils/cn';

interface MfdZoomSliderProps {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  onReset: () => void;
  /** Where you are now, e.g. "2.5x" or an axis top of "25,000". */
  readout: string;
  /** Two or three characters under the slider naming what it controls. */
  caption: string;
  /** Accessible name, e.g. "Zoom level" or "Magnify small bars". */
  label: string;
  resetLabel: string;
  /** Spoken instead of the raw number by screen readers. */
  valueText?: string;
  /** Rendered next to the readout, e.g. a "bars are clipped" caret. */
  badge?: React.ReactNode;
  /** 'tall' gives the map's taller travel; charts use the default. */
  size?: 'default' | 'tall';
  className?: string;
}

// The wrapper height and the input width must stay in step: the input is rotated
// a quarter turn, so its WIDTH is what you see as the slider's length.
const TRACK: Record<'default' | 'tall', { box: string; track: string }> = {
  default: { box: 'h-32', track: 'w-32' },
  // 'tall' is sized for the map, which is up to 900px high: a short track next
  // to it left most of the control as empty box.
  tall: { box: 'h-[26rem]', track: 'w-[26rem]' },
};

export const ZoomSlider = ({
  value,
  min,
  max,
  step,
  onChange,
  onReset,
  readout,
  caption,
  label,
  resetLabel,
  valueText,
  badge,
  size = 'default',
  className,
}: MfdZoomSliderProps) => (
  <div
    className={cn(
      // Fixed width: the readout changes as you drag, and without it the control
      // resized on every step and shoved the chart sideways. Side padding is
      // minimal - the buttons and the rotated track are only 28px wide, so
      // anything more is dead space either side of them.
      'flex w-[84px] shrink-0 flex-col items-center gap-1.5 rounded-lg',
      'border border-border bg-card/95 px-1 py-1.5 shadow-sm',
      className,
    )}
  >
    <Button
      variant="outline"
      size="icon"
      className="h-7 w-7"
      aria-label={`${label}: zoom in`}
      disabled={value >= max}
      onClick={() => onChange(Math.min(max, value + step))}
    >
      <Plus className="h-3.5 w-3.5" />
    </Button>

    <div className={cn('flex w-7 items-center justify-center', TRACK[size].box)}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
        aria-orientation="vertical"
        aria-valuetext={valueText}
        className={cn(
          // shrink-0 is load-bearing. The track's WIDTH is what you see as its
          // length once it is rotated, but the input is a flex item inside a
          // 28px-wide wrapper, so without this the browser shrinks it back to
          // roughly the wrapper's width and the box grows while the line does not.
          'h-1.5 shrink-0 -rotate-90 cursor-pointer appearance-none rounded-full bg-primary/25 accent-primary',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          TRACK[size].track,
        )}
      />
    </div>

    <Button
      variant="outline"
      size="icon"
      className="h-7 w-7"
      aria-label={`${label}: zoom out`}
      disabled={value <= min}
      onClick={() => onChange(Math.max(min, value - step))}
    >
      <Minus className="h-3.5 w-3.5" />
    </Button>

    <Button
      variant="outline"
      size="icon"
      className="h-7 w-7"
      aria-label={resetLabel}
      title={resetLabel}
      onClick={onReset}
    >
      <Maximize2 className="h-3.5 w-3.5" />
    </Button>

    {/* Wraps rather than widening: the combined chart's readout carries two
        axis tops, which would otherwise stretch the whole control. */}
    <span className="flex w-full flex-wrap items-center justify-center gap-0.5 text-center text-[10px] font-bold leading-tight tabular-nums text-foreground">
      {badge}
      {readout}
    </span>
    <span className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
      {caption}
    </span>
  </div>
);
